// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import Search from 'components/header/Search';
import PageHeader from 'components/treactui-template/organisms/page/PageHeader';
import PageWrapper from 'components/treactui-template/organisms/page/PageWrapper';
import useMediaQuery, { DefaultBreakpoints } from 'hooks/useMediaQuery';
import FilterGridContainer, {
  ActionButtonShell,
  PageTitleShell,
  SearchShell,
  SecondRowShell,
} from 'layout/header/FilterGridContainer';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { AppRoutes } from 'utilities/router/AppRoutes';
import { Button } from '@tls/treact-ui';
import { IconMoveLeft, IconPlus } from '@tls/treact-ui/icons';

export default function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { width } = useMediaQuery();
  const { pathname } = useLocation();

  // notification settings bring their own filters
  let filterActions: ReactElement | undefined =
    pathname === AppRoutes.SettingsNotifications ? undefined : (
      <SearchShell>
        <Search />
      </SearchShell>
    );

  if (filterActions && width < DefaultBreakpoints.md) {
    filterActions = <SecondRowShell key='second-row'>{filterActions}</SecondRowShell>;
  }

  const returnUrl = useAppSelector(a => a.uiSettings.returnUrl) ?? AppRoutes.Projects;

  let addButton: ReactElement | undefined = undefined;
  if (pathname === AppRoutes.SettingsUser) {
    addButton = (
      <ActionButtonShell>
        <Button
          label={t('settings.users.add')}
          icon={<IconPlus />}
          onClick={() => navigate(AppRoutes.SettingsAddUser)}
          data-testid='settings-users-add-button'
        />
      </ActionButtonShell>
    );
  } else if (pathname === AppRoutes.SettingsRole) {
    addButton = (
      <ActionButtonShell>
        <Button
          label={t('settings.roles.add')}
          icon={<IconPlus />}
          onClick={() => navigate(AppRoutes.SettingsAddRole)}
          data-testid='settings-roles-add-button'
        />
      </ActionButtonShell>
    );
  }

  let title = t('settings.users.title');
  if (pathname === AppRoutes.SettingsAddUser) {
    title = t('settings.users.add');
  } else if (pathname === AppRoutes.SettingsContracts) {
    title = t('settings.contracts.title');
  } else if (pathname === AppRoutes.SettingsRole) {
    title = t('settings.roles.title');
  } else if (pathname === AppRoutes.SettingsAddRole) {
    title = t('settings.roles.add');
  } else if (pathname === AppRoutes.Versions) {
    title = t('settings.versions.title');
  } else if (pathname === AppRoutes.SettingsNotifications) {
    title = t('settings.notifications.title');
  }

  return (
    <PageWrapper>
      <PageHeader>
        <FilterGridContainer
          $pxBreakpoint={DefaultBreakpoints.md}
          $showActionButton={addButton !== undefined}
          $showView={false}
          $showFilter={false}
        >
          <PageTitleShell>
            <Title>
              <Button
                variant='transparent'
                noShadow
                noBorder
                icon={<IconMoveLeft />}
                onClick={() => navigate(returnUrl)}
                data-testid='settings-back-button'
              />
              <span className='font-size_l'>{title}</span>
            </Title>
          </PageTitleShell>
          {addButton}
          {filterActions}
        </FilterGridContainer>
      </PageHeader>
      <Outlet />
    </PageWrapper>
  );
}

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
